import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { mokedNumber } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { HotAirBalloon, OtpLoginCloseButton } from 'Images';
import { ArrowButton } from 'Modules/OTPLogin/ArrowButton';
import { useNavigate } from 'react-router-dom';

interface IInvalidUserDialog {
  isOpen: boolean;
  closeDialog: () => void;
}

export const InvalidUserDialog = ({
  isOpen,
  closeDialog,
}: IInvalidUserDialog) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.OTP_Login
  ).invalidUserDialog;
  const isDesktop = useIsDesktop();
  const themePallete = useTheme().palette;

  const navigate = useNavigate();

  return (
    <Dialog
      open={isOpen}
      maxWidth='lg'
      BackdropProps={{
        sx: { bgcolor: 'text.secondary', opacity: '0.8 !important' },
      }}
      role='alertdialog'
      aria-labelledby='dialog-title'
      aria-describedby='dialog-description'
      aria-modal='true'
      PaperProps={{ role: 'alertdialog', 'aria-modal': true }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: '24px 12px 0 0',
          bgcolor: 'primary.shade_100',
        }}
      >
        <IconButton onClick={closeDialog}>
          <Avatar
            alt={Dictionary[DICTIONARY.General].close}
            src={OtpLoginCloseButton}
            sx={{
              width: '40px',
              height: '40px',
            }}
          />
        </IconButton>
      </Box>
      <DialogContent
        sx={{ padding: 0, pb: '48px', bgcolor: 'primary.shade_100' }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: isDesktop ? '0 100px' : undefined,
            gap: '12px',
          }}
        >
          <Avatar
            sx={{
              width: isDesktop ? '160px' : '125px',
              height: isDesktop ? '120px' : '93px',
              mb: '12px',
            }}
            src={HotAirBalloon}
            alt=''
          />
          <Typography
            id='dialog-title'
            variant={'h2'}
            maxWidth={isDesktop ? 'initial' : '285px'}
          >
            {dictionary.title}
          </Typography>
          <Typography id='dialog-description' variant={'body3'}>
            {dictionary.subtitle}
          </Typography>
          <Stack
            direction='row'
            alignItems='flex-end'
            justifyContent='center'
            gap='6px'
          >
            <Typography variant={'body3'}>{dictionary.helpLineText}</Typography>
            {isDesktop ? (
              <Typography variant={'body3'}>{mokedNumber}</Typography>
            ) : (
              <Link href={`tel:${mokedNumber}`}>
                <Typography variant={'body3'}>{mokedNumber}</Typography>
              </Link>
            )}
          </Stack>
          <Stack
            direction='row'
            alignItems='flex-end'
            justifyContent='center'
            gap='6px'
          >
            <Typography variant={'body3'}>
              {dictionary.moreWaysToContact}
            </Typography>
            {
              <Link href={'https://shikum.mod.gov.il/contactus'}>
                <Typography variant={'body3'}>{dictionary.findHere}</Typography>
              </Link>
            }
          </Stack>
          <Stack alignItems={'center'}>
            <Stack
              flexDirection={isDesktop ? 'row' : 'column'}
              alignItems='center'
              justifyContent='center'
              gap={isDesktop ? '16px' : '24px'}
              mt='64px'
              mb={isDesktop ? '20px' : '0'}
              maxWidth={isDesktop ? undefined : '208px'}
            >
              <Box width={'100%'} height={isDesktop ? '56px' : '44px'}>
                {isDesktop && (
                  <ArrowButton
                    text={dictionary.toPersonalArea}
                    arrowColor={'#fff'}
                    buttonVariant={'primary1'}
                    typographyVariant={'body1'}
                    arrowPosition={'left'}
                    onClick={() => navigate('/')}
                  />
                )}
                {!isDesktop && (
                  <ArrowButton
                    text={dictionary.toPersonalArea}
                    arrowColor={'#fff'}
                    buttonVariant={'primary1'}
                    typographyVariant={'body4'}
                    arrowPosition={'left'}
                    onClick={() => navigate('/')}
                  />
                )}
              </Box>
              <Box height={isDesktop ? '56px' : '44px'} width={'100%'}>
                {isDesktop && (
                  <ArrowButton
                    text={dictionary.backToEdit}
                    buttonVariant={'secondary1'}
                    arrowColor={themePallete.primary.main}
                    typographyVariant={'body4'}
                    arrowPosition={'right'}
                    onClick={closeDialog}
                  />
                )}
                {!isDesktop && (
                  <ArrowButton
                    text={dictionary.backToEdit}
                    arrowColor={themePallete.primary.main}
                    buttonVariant={'secondary1'}
                    typographyVariant={'body4'}
                    arrowPosition={'right'}
                    onClick={closeDialog}
                  />
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
